import { FC } from "react";

import { Table } from "antd";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { desktopFundColumns, mobileFundColumns, altQuoteDesktopFundColumns } from "./constants";
import { useScreen } from "common/shared-hooks";
import { currencyFormatter } from "domains/liquidity/shared/utils/inputValueFormatter";
import { IFundAsset } from "domains/liquidity/shared/types";
import { Spinner, Button } from "common/lib/components";
import { useAltQuotesSelector } from "domains/altQuotes";
import { liquidityApiReducer } from "domains/liquidity/store/liquidity.api.reducer";

import "./FundAssetsTable.scss";

interface IProps {
  title: string;
  fundAssets: IFundAsset[];
  onDelete: (asset_id: string, LRId: string) => void;
}

const FundAssetsTable: FC<IProps> = ({ title, fundAssets, onDelete }) => {
  const { t } = useTranslation([ "liquidity", "common" ]);
  const { mobile } = useScreen();
  const { pathname } = useLocation();

  const { isAltQuoteTab } = useAltQuotesSelector();
  const [ _, { isLoading: isDeletingLRAsset } ] = liquidityApiReducer.useDeleteLRAssetMutation({
    fixedCacheKey: "delete-lr-asset",
  });

  const isAltQuotePage = pathname.includes('altquote');  // Check if on AltQuote page
  const isReviewAssetsPage = pathname.includes('review_assets');  // Check if on review assets page
  const desktopColumns = isAltQuoteTab ? altQuoteDesktopFundColumns : desktopFundColumns;
  const mobileColumns = (isAltQuotePage && !isReviewAssetsPage) ? mobileFundColumns.filter((column) => column.key !== "action") : mobileFundColumns;  // Hide action column on mobile for AltQuote page if not review_assets
  const columns = mobile ? mobileColumns : desktopColumns;
  const hideDelete = pathname.includes('altquote-assets-included');
  let filteredColumns = columns;
  if (hideDelete) filteredColumns = columns.filter((col: any) => col.key !== 'action');


  const fundDataSource = fundAssets.map((asset, i) => ({
    key: i,
    fund: (
      <div>
        <b>
          <span>{asset.asset_name}</span>
        </b>
        <br />
        <span>{asset.fund_manager}</span>
        <br />
        <span className="ben-fund-assets-text">NAV: </span>
        {currencyFormatter(asset.current_net_asset_value)}
        {isAltQuoteTab && (
          <>
            <br />
            <span className="ben-fund-assets-text">Unfunded Commitments: </span>
            {currencyFormatter(asset.unfunded_commitment_currency)}
          </>
        )}
      </div>
    ),
    fundManager: asset.fund_manager || asset.sponsor,
    fundName: asset.asset_name,
    nav: currencyFormatter(asset.current_net_asset_value),
    unfundedCommitments: currencyFormatter(asset.unfunded_commitment_currency),
    action: hideDelete ? null : (
      <div style={ { display: "inline-block" } }>
        <Spinner
          spinning={
            isDeletingLRAsset || asset.asset_id.startsWith("pending_creation") || asset.asset_id.startsWith("creating")
          }
        >
          <Button
            // @ts-ignore
            onClick={ () => (onDelete(asset.asset_id)) }
            type="primary"
          >
            {t("Delete", { ns: "common" })}
          </Button>
        </Spinner>
      </div>
    ),
  }));

  
  if (!fundAssets.length) return null;
  return (
    <div className="ben-form-table-container">
      <span className="fund-assets-table-title">{title}</span>

      <Table pagination={ false } columns={ filteredColumns } dataSource={ fundDataSource } className="ben-fund-assets-table" />
    </div>
  );
};

export default FundAssetsTable;
